import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAppTracking from 'hooks/useAppTracking';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import fetchLanguageResource from 'services/common/fetchLanguageResource';
import { ENUMS, SMS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const largeDefaultColSpan = {
	md: 12
};

const defaultColSpan = {
	md: 6
};

const FreeTrackingSmsForm = NiceModal.create(({ id, mode, modalName }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { LanguageCode } = useLocale();
	const axios = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const { closeModal } = useModalRouter();
	const { track } = useAppTracking();

	const [isMinimizing, setIsMinimizing] = useState(false);
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					smsType: yup.string().nullable().required(),
					definition: yup.string().nullable(),
					smsConfigID: yup.string().nullable(),
					language: yup.string().nullable(),
					content: yup.string().nullable().required(),
					status: yup.string().nullable().required(),
					brandID: yup.string().nullable(),
					waitingAddressConfirmationTime: yup.number().nullable().min(0)
				})
				.required(),
		[]
	);

	const fillForm = async () => {
		const defaultValues = {
			smsType: 'trackingSms',
			definition: null,
			smsConfigID: null,
			brandID: null,
			language: LanguageCode ?? null,
			content: null,
			status: 'active',
			waitingAddressConfirmationTime: 30
		};
		if (!id) {
			return defaultValues;
		}
		try {
			const response = await axios.post(SMS.GET_SMS_SETTINGS, {
				ID: id
			});

			const { data } = response;
			return {
				...defaultValues,
				...data
			};
		} catch (error) {
			AppLogger.error(error);
		}
	};

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isDirty, isSubmitting, isSubmitSuccessful, errors, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful && !isMinimizing);

	const smsType = watch('smsType');
	const languageValue = watch('language');

	const handleCloseModal = () => {
		closeModal();
	};

	useEffect(() => {
		fetchLanguageResource(languageValue).then(() => {
			const val = t('smsContentFreeDefault', {
				lng: languageValue
			});
			setValue('content', val, { shouldValidate: true });
		});
	}, [languageValue]);

	const onSubmit = async (data) => {
		const URL = mode === 'edit' ? SMS.UPDATE_SMS_SETTINGS : SMS.ADD_SMS_SETTINGS;
		try {
			const response = await axios.post(URL, {
				ID: id || undefined,
				...data,
				smsConfigID: data.smsConfigID,
				brandID: data.brandID,
				language: data.language
			});

			if (response.status === 200) {
				dispatch(updateDynamicListData(DYNAMICLISTS.SMS_SETTINGS));
				handleCloseModal();
				showSuccess();
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			if (error.response?.data.code === 'error.B010') {
				showError({
					header: 'errorMsg.sameTemplateAlreadyExist',
					message: 'errorMsg.youCanNotAddSameSMSTypeandBrandandLanguage'
				});
			} else if (error.response?.data.code === 'error.B016') {
				showError({
					header: 'errorMsg.sameTemplateAlreadyExist',
					message: 'errorMsg.youCanNotAddSameManualSmsName'
				});
			} else if (error.response?.data.code === 'error.B015') {
				track('moreThan1SMS');
				showError({
					header: 'errorMsg.freePackageUserOnlyOneSMSHeader',
					message: 'errorMsg.freePackageUserOnlyOneSMSMessage'
				});
			} else {
				AppLogger.error(error);
				showError({
					message: error?.response?.data?.code || error?.response?.data?.message
				});
			}
		}
	};

	return (
		<Modal
			fullwidth
			maxWidth="medium"
			name={modalName}
			className="modal-smsSetting"
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
						setIsMinimizing(false);
					}}
					onClose={close}
					label={mode === 'edit' ? 'editFreeTrackingSms' : 'addFreeTrackingSms'}
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}>
			<ModalTitle>
				<Typography variant="p1">{mode === 'edit' ? 'editFreeTrackingSms' : 'addFreeTrackingSms'}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="smsType" colSpan={defaultColSpan} isLoading={isLoading} required>
						<EnumSelect
							searchable={false}
							disabled
							data={['trackingSms']}
							value={smsType}
							name={`smsType`}
							setValue={(value) => {
								setValue(`smsType`, value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>
					<FormItem label="language" colSpan={defaultColSpan} isLoading={isLoading} required>
						<EnumSelect
							searchable={false}
							valueRemovable={false}
							enumName={ENUMS.LANGUAGE_CODES}
							value={watch(`language`)}
							setValue={(value) => {
								setValue(`language`, value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>

					<FormItem label="status" colSpan={defaultColSpan} isLoading={isLoading} required>
						<EnumSelect
							searchable={false}
							enumName={ENUMS.ACTIVE_INACTIVE}
							name={`status`}
							valueRemovable={false}
							value={watch(`status`)}
							setValue={(value) => {
								setValue(`status`, value, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
						/>
					</FormItem>

					<FormItem
						label="smsContent"
						toolTip="smsContent.description"
						colSpan={largeDefaultColSpan}
						isLoading={isLoading}
						required>
						<TextArea
							name="content"
							errorMessage={errors?.content?.message}
							disabled
							placeholder="smsContent.placeholder"
							maxlength={1600}
						/>
					</FormItem>
					{!isLoading && (
						<FormItem colSpan={largeDefaultColSpan}>
							<Alert showIcon content="freeTrackingInfoText" />
						</FormItem>
					)}
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				<Button
					label={mode === 'edit' ? 'save' : 'button.add'}
					loading={isLoading}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					iconPos="right"
				/>
			</ModalActions>
		</Modal>
	);
});
export default FreeTrackingSmsForm;
